import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Brain, Sparkles } from 'lucide-react';

import { PlanType } from '@optidev/shared-types';
import { usePlanFeatures } from '@/hooks/useFeatures';
import { billingApi } from '@/lib/api/billing';
import { auth } from '@/lib/firebase';

export type ModelType = 'claude-4-sonnet' | 'claude-4-haiku' | 'claude-4-opus';

interface ModelDropdownProps {
  model: ModelType;
  onChange: (model: ModelType) => void;
  disabled?: boolean;
}

// Map ModelType to planFeatures model key
const modelToPlanKey: Record<ModelType, string> = {
  'claude-4-opus': 'opus',
  'claude-4-sonnet': 'sonnet',
  'claude-4-haiku': 'haiku',
};

const modelConfig = {
  'claude-4-opus': {
    label: 'Premium',
    modelName: 'claude-4.5-opus',
  },
  'claude-4-sonnet': {
    label: 'Quality',
    modelName: 'claude-4.5-sonnet',
  },
  'claude-4-haiku': {
    label: 'Fast',
    modelName: 'claude-4.5-haiku',
  },
};

export function ModelDropdown({
  model,
  onChange,
  disabled = false,
}: ModelDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { planFeatures } = usePlanFeatures();
  const [planType, setPlanType] = useState<PlanType>(PlanType.FREE);

  // Fetch user's subscription plan
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const token = await auth.getIdToken();
        if (token) {
          const subscription = await billingApi.getSubscription(token);
          setPlanType(subscription.planType);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
        setPlanType(PlanType.FREE);
      }
    };
    fetchPlan();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if a model is allowed for the current plan
  const isModelAllowed = (modelKey: ModelType): boolean => {
    const currentPlanFeatures = planFeatures[planType] || planFeatures.free;
    const planModelKey = modelToPlanKey[modelKey];
    return currentPlanFeatures.models.includes(planModelKey);
  };

  const handleSelect = (newModel: ModelType) => {
    if (!isModelAllowed(newModel)) {
      return; // Don't allow selection of restricted models
    }
    onChange(newModel);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-1 px-2 py-1 rounded-md
          bg-muted hover:bg-accent text-foreground
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          transition-all duration-200 text-xs font-medium
        `}
      >
        <Brain className="w-3 h-3" />
        <span>{modelConfig[model].label}</span>
        <ChevronDown className={`w-2.5 h-2.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 w-56 bg-popover rounded-lg shadow-lg border border-border z-50">
          <div className="p-1">
            {Object.entries(modelConfig).map(([key, config]) => {
              const modelKey = key as ModelType;
              const isSelected = model === modelKey;
              const canUse = isModelAllowed(modelKey);

              return (
                <div key={key}>
                  <button
                    type="button"
                    onClick={() => canUse && handleSelect(modelKey)}
                    disabled={!canUse}
                    className={`
                      w-full flex items-center justify-between px-3 py-2 rounded-md
                      ${!canUse ? 'opacity-60 cursor-not-allowed' : isSelected ? 'bg-accent' : 'hover:bg-accent cursor-pointer'}
                      transition-colors duration-150
                    `}
                  >
                    <div className="flex flex-col items-start">
                      <span className="text-sm text-foreground">{config.label}</span>
                      <span className="text-xs text-muted-foreground">{config.modelName}</span>
                      {!canUse && (
                        <Link
                          to="/dashboard/settings/billing"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(false);
                          }}
                          className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-purple-600 dark:text-purple-400 hover:underline"
                        >
                          <Sparkles className="h-3 w-3" />
                          Upgrade
                        </Link>
                      )}
                    </div>
                    {isSelected && canUse && (
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
